<template>
  <div>
    <div class="coupons-surface">
      <div v-if="loading" class="px-3 py-4 text-xs-center">Loading...</div>

      <div v-else-if="!coupons.length" class="px-3 py-3">
        <div class="mb-2">No coupons found for this account.</div>
        <v-btn small color="success" class="my-2" @click="openCreateDialog"
          >Create</v-btn
        >
      </div>

      <div v-else class="px-3 py-3">
        <div
          v-for="(coupon, index) in coupons"
          :key="coupon.id || `coupon-${index}`"
          class="coupon-row"
        >
          <v-layout row wrap>
            <v-flex xs12 sm8>
              <div class="coupon-title mb-2">
                Coupon #{{ coupon.couponNumber || "-" }}
              </div>
              <div class="field-row">
                <span class="field-label">Date</span>
                <span class="field-value">{{ formatDate(coupon.date) }}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Created by</span>
                <span class="field-value">{{ coupon.createdBy || "-" }}</span>
              </div>
            </v-flex>
            <v-flex xs12 sm4 class="mt-2 mt-sm-0">
              <div class="amount-label">Amount</div>
              <div class="amount-value">
                {{ formatCurrency(coupon.amount) }}
              </div>
            </v-flex>
            <v-flex xs12>
              <div class="coupon-note mt-2">
                <div class="note-label">Note</div>
                <div class="note-value">{{ coupon.note || "-" }}</div>
              </div>
            </v-flex>
            <v-flex xs12 class="mt-2">
              <div class="meta-row">
                <span class="meta-chip">
                  Emailed: {{ formatDate(coupon.emailedAt) }}
                </span>
                <span class="meta-chip">
                  Downloaded: {{ formatDate(coupon.downloadedAt) }}
                </span>
              </div>
            </v-flex>
          </v-layout>

          <v-layout row wrap class="mt-3">
            <v-btn
              small
              outline
              color="success"
              class="mr-2 mb-2"
              @click="openEmailDialog(coupon)"
            >
              Email Customer
            </v-btn>
            <v-btn
              small
              outline
              color="success"
              class="mr-2 mb-2"
              @click="openBakeryDialog(coupon)"
            >
              Send To Bakery
            </v-btn>
            <v-btn
              small
              outline
              color="info"
              class="mb-2"
              @click="openDownloadDialog(coupon)"
            >
              Download
            </v-btn>
          </v-layout>

          <v-divider v-if="index + 1 < coupons.length" class="mt-4"></v-divider>
        </div>
      </div>

      <v-divider></v-divider>

      <div class="px-3 py-3">
        <div class="font-weight-medium mb-3">Action Logs</div>
        <div v-if="!printCards.length" class="caption">
          No action logs found.
        </div>
        <div v-else>
          <div
            v-for="(card, index) in printCards"
            :key="card.id || index"
            class="action-log-entry"
          >
            <span class="action-log-date mr-3 pl-0">{{
              card.cardPrintDate | time
            }}</span>
            <v-icon
              small
              class="action-log-icon mr-1"
              :color="getActionIconColor(card.actionType)"
            >
              {{ getActionIcon(card.actionType) }}
            </v-icon>
            <span class="action-log-action">{{ card.actionType || "-" }}</span>
            <span class="action-log-user ml-3"
              >by {{ card.byUser || "-" }}</span
            >
            <v-divider
              v-if="index + 1 < printCards.length"
              class="mt-2"
            ></v-divider>
          </div>
        </div>
      </div>
    </div>

    <v-dialog v-model="createDialog" max-width="400">
      <v-card class="pa-2">
        <v-card-title class="headline">Create Coupon</v-card-title>
        <v-card-text class="pb-0">
          <v-text-field
            ref="couponAmount"
            v-model="createCouponForm.amount"
            label="Amount"
            prefix="$"
            type="number"
            :rules="amountRules"
            disabled
          ></v-text-field>
          <v-text-field
            ref="couponNote"
            v-model="createCouponForm.note"
            label="Note"
            :rules="requiredRules"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="createDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="success" :loading="processing" @click="createCoupon"
            >Create</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="emailDialog" max-width="460">
      <v-card class="pa-2">
        <v-card-title class="headline pb-1"
          >Send Coupon To Customer</v-card-title
        >
        <v-card-text class="pt-2 pb-2">
          <div class="caption mb-2">
            Send this coupon directly to the customer.
          </div>
          <div class="dialog-summary mb-3">
            <v-layout row justify-space-between align-center>
              <div>
                <div class="download-summary-label">Coupon</div>
                <div class="download-summary-value">
                  #{{ selectedCoupon.couponNumber || "-" }}
                </div>
              </div>
              <div class="text-xs-right">
                <div class="download-summary-label">Amount</div>
                <div class="download-summary-value">
                  {{ formatCurrency(selectedCoupon.amount) }}
                </div>
              </div>
            </v-layout>
          </div>
          <v-text-field
            ref="couponEmail"
            v-model="emailForm.email"
            label="Email"
            :rules="emailRules"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pt-1">
          <v-spacer></v-spacer>
          <v-btn color="default" dark class="mr-2" @click="emailDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="success" :loading="processing" @click="sendCouponEmail"
            >Send</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="bakeryDialog" max-width="460">
      <v-card class="pa-2">
        <v-card-title class="headline"
          >Send Coupon To Matzo Bakery</v-card-title
        >
        <v-card-text class="pt-2 pb-2">
          <div class="caption mb-2">
            Confirm sending this coupon to Matzo Bakery.
          </div>
          <div class="dialog-summary">
            <v-layout row justify-space-between align-center>
              <div>
                <div class="download-summary-label">Coupon</div>
                <div class="download-summary-value">
                  #{{ selectedCoupon.couponNumber || "-" }}
                </div>
              </div>
              <div class="text-xs-right">
                <div class="download-summary-label">Amount</div>
                <div class="download-summary-value">
                  {{ formatCurrency(selectedCoupon.amount) }}
                </div>
              </div>
            </v-layout>
          </div>
        </v-card-text>
        <v-card-actions class="pt-3 pr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark class="mr-2" @click="bakeryDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            :loading="processing"
            @click="sendCouponToBakery"
            >Confirm</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="downloadDialog" max-width="460">
      <v-card class="pa-2">
        <v-card-title class="headline pb-1">Download Coupon</v-card-title>
        <v-card-text class="pt-2 pb-2">
          <div class="caption mb-2">
            A Coupon will download to your device.
          </div>
          <div class="download-summary">
            <v-layout row justify-space-between align-center>
              <div>
                <div class="download-summary-label">Coupon</div>
                <div class="download-summary-value">
                  #{{ selectedCoupon.couponNumber || "-" }}
                </div>
              </div>
              <div class="text-xs-right">
                <div class="download-summary-label">Amount</div>
                <div class="download-summary-value">
                  {{ formatCurrency(selectedCoupon.amount) }}
                </div>
              </div>
            </v-layout>
          </div>
        </v-card-text>
        <v-card-actions class="pt-3">
          <v-spacer></v-spacer>
          <v-btn
            color="default"
            dark
            class="mr-2"
            @click="downloadDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="info" :loading="processing" @click="downloadCoupon"
            >Download</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";

export default {
  props: {
    accountID: String,
    accountEmail: String,
  },
  data() {
    return {
      loading: false,
      processing: false,
      coupons: [],
      printCards: [],

      createDialog: false,
      emailDialog: false,
      bakeryDialog: false,
      downloadDialog: false,
      selectedCoupon: {},
      createCouponForm: {
        amount: "",
        note: "",
      },
      emailForm: {
        email: "",
      },
      requiredRules: [(v) => !!v || "Required"],
      amountRules: [
        (v) => !!v || "Required",
        (v) => Number(v) > 0 || "Amount must be greater than 0",
      ],
      emailRules: [
        (v) => !!v || "Required",
        (v) => /.+@.+\..+/.test(v) || "Email must be valid",
      ],
    };
  },
  created() {
    this.loadData();
    if (this.accountEmail) {
      this.emailForm.email = this.accountEmail;
    }
  },
  watch: {
    accountEmail(newValue) {
      if (!this.emailForm.email) {
        this.emailForm.email = newValue || "";
      }
    },
  },
  methods: {
    async loadData() {
      if (!this.accountID) return;

      try {
        this.loading = true;
        const [coupons, printCards] = await Promise.all([
          this.$http.get(
            "api/matzoCoupons/GetForAccount?accountID=" + this.accountID
          ),
          this.$http.get(
            "api/matzoCoupons/GetPrintCardsForAccount?accountID=" +
              this.accountID
          ),
        ]);

        this.coupons = Array.isArray(coupons) ? coupons : [];
        this.printCards = Array.isArray(printCards) ? printCards : [];
      } catch (e) {
        this.$notify({
          group: "notification",
          text: "Failed to load coupons",
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      if (!value) return "-";
      return moment(value).format("LLL");
    },
    formatCurrency(value) {
      if (value === null || value === undefined || value === "") return "-";
      const amount = Number(value);
      if (Number.isNaN(amount)) return "-";
      return amount.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    },
    getActionIcon(actionType) {
      const action = (actionType || "").toUpperCase();

      if (action === "SEND_TO_BAKERY") return "mdi-store";
      if (action === "SEND_TO_ACCOUNT") return "mdi-email";
      if (action.includes("EMAIL")) return "mdi-email";
      if (action.includes("DOWNLOAD")) return "mdi-download";
      if (action.includes("BAKERY")) return "mdi-store";
      if (action.includes("CREATE")) return "mdi-plus-circle-outline";

      return "mdi-history";
    },
    getActionIconColor(actionType) {
      const action = (actionType || "").toUpperCase();

      if (action === "SEND_TO_BAKERY") return "warning";
      if (action === "SEND_TO_ACCOUNT") return "success";
      if (action.includes("EMAIL")) return "success";
      if (action.includes("DOWNLOAD")) return "info";
      if (action.includes("BAKERY")) return "warning";
      if (action.includes("CREATE")) return "primary";

      return "grey";
    },
    openCreateDialog() {
      this.createCouponForm.amount = "100";
      this.createCouponForm.note = "";
      this.createDialog = true;

      this.$nextTick(() => {
        if (this.$refs.couponAmount) this.$refs.couponAmount.resetValidation();
        if (this.$refs.couponNote) this.$refs.couponNote.resetValidation();
      });
    },
    async createCoupon() {
      if (!this.$refs.couponAmount.validate(true)) return;
      if (!this.$refs.couponNote.validate(true)) return;

      const note = (this.createCouponForm.note || "").trim();
      if (!note) return;

      try {
        this.processing = true;
        await this.$http.post("api/matzoCoupons/Create", {
          accountID: this.accountID,
          amount: Number(this.createCouponForm.amount),
          note,
          createdBy: this.$store.state.authentication.user,
        });

        this.createDialog = false;
        this.$notify({
          group: "notification",
          text: "Coupon created",
          type: "success",
        });
        await this.loadData();
      } catch (e) {
        this.$notify({
          group: "notification",
          text: "Failed to create coupon",
          type: "error",
        });
      } finally {
        this.processing = false;
      }
    },
    openEmailDialog(coupon) {
      this.selectedCoupon = coupon || {};
      this.emailForm.email = this.accountEmail || "";
      this.emailDialog = true;

      this.$nextTick(() => {
        if (this.$refs.couponEmail) this.$refs.couponEmail.resetValidation();
      });
    },
    openBakeryDialog(coupon) {
      this.selectedCoupon = coupon || {};
      this.bakeryDialog = true;
    },
    openDownloadDialog(coupon) {
      this.selectedCoupon = coupon || {};
      this.downloadDialog = true;
    },
    async sendCouponEmail() {
      if (!this.$refs.couponEmail.validate(true)) return;

      try {
        this.processing = true;
        const user = this.$store.state.authentication.user || "";
        await this.$http.post(
          "api/matzoCoupons/SendEmailToAccount?accountID=" +
            this.accountID +
            "&user=" +
            user
        );
        this.$notify({
          group: "notification",
          text: "Coupon email sent successfully",
          type: "success",
        });
        this.emailDialog = false;
        await this.loadData();
      } catch (e) {
        this.$notify({
          group: "notification",
          text: "Failed to send coupon email",
          type: "error",
        });
      } finally {
        this.processing = false;
      }
    },
    async sendCouponToBakery() {
      try {
        this.processing = true;
        const user = this.$store.state.authentication.user || "";
        await this.$http.post(
          "api/matzoCoupons/SendEmailToBakery?AccountID=" +
            this.accountID +
            "&user=" +
            user
        );
        this.$notify({
          group: "notification",
          text: "Coupon was sent to Matzo Bakery",
          type: "success",
        });
        this.bakeryDialog = false;
        await this.loadData();
      } catch (e) {
        this.$notify({
          group: "notification",
          text: "Failed to send coupon to Matzo Bakery",
          type: "error",
        });
      } finally {
        this.processing = false;
      }
    },
    async downloadCoupon() {
      try {
        this.processing = true;
        const user = this.$store.state.authentication.user || "";
        const couponID = this.selectedCoupon.id || this.selectedCoupon.ID;
        if (!couponID) {
          throw new Error("Missing coupon id");
        }

        const file = await this.$http.get(
          "api/matzoCoupons/DownloadCouponPdf?id=" + couponID + "&user=" + user,
          {
            responseType: "blob",
          }
        );

        const blob = new Blob([file], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `coupon-${couponID}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$notify({
          group: "notification",
          text: "Coupon download started",
          type: "success",
        });
        this.downloadDialog = false;
        await this.loadData();
      } catch (e) {
        this.$notify({
          group: "notification",
          text: "Failed to download coupon",
          type: "error",
        });
      } finally {
        this.processing = false;
      }
    },
  },
};
</script>

<style>
.coupons-surface {
  border: 1px solid #e4e7eb;
  border-radius: 6px;
  background: #fff;
}

.coupon-row {
  border-radius: 4px;
  padding: 10px 6px;
}

.coupon-title {
  font-size: 21px;
  font-weight: 600;
  color: #1f2937;
}

.field-row {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
}

.field-label {
  min-width: 78px;
  font-size: 13px;
  color: #6b7280;
}

.field-value {
  font-size: 14px;
  color: #111827;
}

.amount-label {
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.amount-value {
  font-size: 22px;
  font-weight: 600;
  color: #111827;
}

.coupon-note {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 10px;
}

.note-label {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.note-value {
  font-size: 14px;
  color: #111827;
  line-height: 1.4;
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.meta-chip {
  font-size: 12px;
  color: #4b5563;
  background: #f3f4f6;
  border-radius: 999px;
  padding: 4px 10px;
}

.action-log-entry {
  padding: 8px 0;
}

.action-log-action {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
}

.action-log-icon {
  vertical-align: text-bottom;
}

.action-log-user {
  font-size: 13px;
  color: #6b7280;
}

.action-log-date {
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
  padding-left: 12px;
}

.download-summary {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f8fafc;
  padding: 10px 12px;
}

.dialog-summary {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f8fafc;
  padding: 10px 12px;
}

.download-summary-label {
  font-size: 12px;
  color: #6b7280;
}

.download-summary-value {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}
</style>
